<template>
    <div class="pivot-table-container">
        <div v-if="!data || !data.rows || data.rows.length === 0" class="empty-state">
            <a-empty description="Pivot tablosu için veri bulunamadı" />
        </div>
        <div v-else class="pivot-table-wrapper">
            <table class="pivot-table">
                <!-- Column Headers -->
                <thead>
                    <tr v-for="(row, rowIndex) in columnHeaderRows" :key="'col-header-' + rowIndex">
                        <!-- Empty cells for row headers -->
                        <th 
                            v-for="n in rowFieldCount" 
                            :key="'empty-' + n"
                            class="empty-corner-cell"
                        >
                            <span v-if="rowIndex === 0 && n === 1">{{ rowFieldsLabel }}</span>
                        </th>
                        
                        <!-- Column header cells -->
                        <th 
                            v-for="(cell, colIndex) in row" 
                            :key="'col-' + colIndex"
                            :colspan="cell.colspan || 1"
                            :rowspan="cell.rowspan || 1"
                        >
                            <pivot-header-cell
                                :label="cell.label"
                                :is-row-header="false"
                                :sortable="false"
                            />
                        </th>
                    </tr>
                </thead>

                <!-- Data Rows -->
                <tbody>
                    <tr v-for="(row, rowIndex) in visibleDataRows" :key="'data-row-' + rowIndex">
                        <!-- Row header cells -->
                        <td 
                            v-for="(headerCell, headerIndex) in visibleHeaders(row.headers)" 
                            :key="'row-header-' + headerIndex"
                            :rowspan="headerCell.rowspan || 1"
                        >
                            <pivot-header-cell
                                :label="headerCell.label"
                                :level="headerCell.level || 0"
                                :expandable="headerCell.hasChildren"
                                :expanded="isRowExpanded(row.id)"
                                :is-row-header="true"
                                @toggle-expand="toggleRowExpand(row.id)"
                            />
                        </td>

                        <!-- Data cells -->
                        <td v-for="(cell, cellIndex) in row.cells" :key="'cell-' + cellIndex">
                            <pivot-cell
                                :value="cell.value"
                                :cell-type="cell.type || 'data'"
                                :color-config="config ? config.colorSettings : null"
                                :min="metadata.min"
                                :max="metadata.max"
                                :decimal-format="displayOptions.decimalFormat"
                                :empty-cell="displayOptions.emptyCell"
                                :tooltip-content="cell.tooltip"
                                :unit="getUnitForCell(cellIndex)"
                            />
                        </td>
                    </tr>

                    <!-- Grand Total Row -->
                    <tr v-if="showTotals && grandTotalRow" class="grand-total-row">
                        <td :colspan="rowFieldCount">
                            <pivot-header-cell
                                label="Genel Toplam"
                                :is-row-header="true"
                            />
                        </td>
                        <td v-for="(cell, cellIndex) in grandTotalRow.cells" :key="'total-' + cellIndex">
                            <pivot-cell
                                :value="cell.value"
                                cell-type="total"
                                :decimal-format="displayOptions.decimalFormat"
                                :empty-cell="displayOptions.emptyCell"
                                :unit="getUnitForCell(cellIndex)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import PivotCell from "./PivotCell.vue";
import PivotHeaderCell from "./PivotHeaderCell.vue";

export default Vue.extend({
    name: "PivotTable",
    components: {
        PivotCell,
        PivotHeaderCell
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        metadata: {
            type: Object,
            default: () => ({ min: 0, max: 0 })
        },
        valueFields: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            expandedRows: [] // Vue 2'de Set reactive değil, Array kullanıyoruz
        };
    },
    computed: {
        rowFieldCount() {
            if (!this.data || !this.data.rowFields) return 0;
            return this.data.rowFields.length;
        },
        rowFieldsLabel() {
            if (!this.data || !this.data.rowFields) return '';
            return this.data.rowFields.map(f => f.name).join(' / ');
        },
        columnHeaderRows() {
            if (!this.data || !this.data.columnHeaders) return [];
            return this.data.columnHeaders;
        },
        visibleDataRows() {
            if (!this.data || !this.data.rows) return [];
            return this.data.rows.filter(row => this.isRowVisible(row));
        },
        showTotals() {
            return this.config && this.config.displayOptions && this.config.displayOptions.showTotals;
        },
        grandTotalRow() {
            if (!this.data || !this.data.grandTotal) return null;
            return this.data.grandTotal;
        },
        displayOptions() {
            return this.config && this.config.displayOptions ? this.config.displayOptions : {
                showTotals: true,
                showSubtotals: true,
                decimalFormat: 2,
                emptyCell: '-',
                compactMode: false
            };
        }
    },
    methods: {
        initializeExpandedRows() {
            // Tüm hasChildren=true olan row'ları başlangıçta expand et
            if (this.data && this.data.rows) {
                const expandedIds = [];
                this.data.rows.forEach(row => {
                    if (row.hasChildren) {
                        expandedIds.push(row.id);
                    }
                });
                this.expandedRows = expandedIds;
                console.log('Initialized expanded rows:', this.expandedRows);
            }
        },
        visibleHeaders(headers) {
            return headers.filter(h => h.visible !== false);
        },
        getUnitForCell(cellIndex) {
            // Her cell index'i için hangi value field'a ait olduğunu bul
            if (!this.valueFields || this.valueFields.length === 0) return null;
            
            const valueFieldIndex = cellIndex % this.valueFields.length;
            const valueField = this.valueFields[valueFieldIndex];
            
            return valueField && valueField.unit ? valueField.unit : null;
        },
        isRowExpanded(rowId) {
            return this.expandedRows.includes(rowId);
        },
        toggleRowExpand(rowId) {
            console.log('Toggle expand for row:', rowId);
            const index = this.expandedRows.indexOf(rowId);
            if (index > -1) {
                // Collapse
                this.expandedRows.splice(index, 1);
                console.log('Collapsed:', rowId);
            } else {
                // Expand
                this.expandedRows.push(rowId);
                console.log('Expanded:', rowId);
            }
            console.log('Current expanded rows:', this.expandedRows);
        },
        isRowVisible(row) {
            // Parent row ise her zaman görünür
            if (!row.parentId) {
                return true;
            }
            
            // Child row ise parent expand edilmişse görünür
            const parentExpanded = this.isRowExpanded(row.parentId);
            
            return parentExpanded;
        }
    },
    mounted() {
        // Başlangıçta tüm satırları genişletilmiş göster
        this.initializeExpandedRows();
    },
    watch: {
        data: {
            handler(newData, oldData) {
                // İlk yükleme veya tamamen yeni data geldiğinde expand'leri yeniden başlat
                if (!oldData || !oldData.rows) {
                    this.initializeExpandedRows();
                }
            },
            deep: true,
            immediate: true
        }
    }
});
</script>

<style scoped lang="scss">
.pivot-table-container {
    width: 100%;
    height: 100%;
    overflow: auto;

    .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        min-height: 300px;
    }

    .pivot-table-wrapper {
        overflow: auto;
        max-height: 100%;

        .pivot-table {
            border-collapse: collapse;
            width: 100%;
            background: #fff;

            thead {
                th {
                    background-color: #fafafa;
                    border: 1px solid #e8e8e8;
                    padding: 0;
                    position: sticky;
                    top: 0;
                    z-index: 3;

                    &.empty-corner-cell {
                        background-color: #f5f5f5;
                        font-weight: 600;
                        font-size: 13px;
                        padding: 8px 12px;
                        text-align: left;
                        color: #666;
                        border: 1px solid #e8e8e8;
                        position: sticky;
                        left: 0;
                        z-index: 4;
                    }
                }
            }

            tbody {
                td {
                    padding: 0;
                    border: 1px solid #e8e8e8;

                    &:has(.pivot-header-cell.is-row-header) {
                        position: sticky;
                        left: 0;
                        background-color: #fafafa;
                        z-index: 2;
                    }
                }

                tr.grand-total-row {
                    td {
                        border-top: 2px solid #d9d9d9;
                    }
                }
            }
        }
    }

    // Scrollbar stilleri
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
}
</style>

